/**
 * FaqBlock — Accordion FAQ section
 */
import { useState, useMemo, useEffect } from "react";
import DOMPurify from "dompurify";
import { SurfaceFaqConfigResponse } from "@/hooks/hub/useGetHub";
import { SurfaceRulesModal } from "./SurfaceRulesModal";
import "./FaqBlock.scss";

interface FaqBlockProps {
    config?: SurfaceFaqConfigResponse;
    sectionTitle?: string;
}

/*
 * The accordion's identity. `_id` is the id when the item has one, but an
 * item authored straight into the block can arrive without one — and every
 * such item then shared the key `undefined`, so opening any one opened all
 * of them. Position is the fallback that cannot collide.
 */
function itemKey(item: { _id?: string }, index: number): string {
    return item._id || `idx-${index}`;
}

export const FaqBlock = ({ config, sectionTitle }: FaqBlockProps) => {
    const [openItems, setOpenItems] = useState<Set<string>>(new Set());
    const [searchQuery, setSearchQuery] = useState("");
    const [rulesOpen, setRulesOpen] = useState(false);
    /* Open on arrival — the questions are visible, each one still folded. */
    const [sectionOpen, setSectionOpen] = useState(true);

    /* Every question starts closed, so the list reads as a scannable index. */
    useEffect(() => {
        setOpenItems(new Set<string>());
    }, [config?.items]);

    /* Keys are assigned before filtering — a search must not renumber them. */
    const filteredItems = useMemo(() => {
        const keyed = (config?.items ?? []).map((item, index) => ({item, key: itemKey(item, index)}));
        if (!searchQuery.trim()) return keyed;
        const q = searchQuery.toLowerCase();
        return keyed.filter(({item}) =>
            item.question.toLowerCase().includes(q) || item.answer.toLowerCase().includes(q));
    }, [config?.items, searchQuery]);

    if (!config) return null;
    const rulesSections = config.rulesSections ?? [];
    const hasRules = rulesSections.length > 0;
    /*
     * Rules alone are a section. The old guard returned null unless there was
     * at least one FAQ, which would have hidden an authored rulebook.
     */
    if (!config.items?.length && !hasRules) return null;

    const toggleItem = (id: string) => {
        setOpenItems(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                if (!config?.allowMultipleOpen) next.clear();
                next.add(id);
            }
            return next;
        });
    };

    /*
     * THE LEGAL COLUMN IS DERIVED FROM THE RULES, not from extra fields.
     *
     * The lead section — the heading-less one that opens every rulebook — is
     * the summary shown inline. A section headed "Eligibility" renders inline
     * as its bullet list. Both still appear in the full modal; this is a
     * summary of the same authored copy, never a second place to write it.
     */
    const leadSection = rulesSections.find(s => !s.heading);
    const eligibilitySection = rulesSections.find(s => (/eligibilit/i).test(s.heading ?? ""));
    const hasLegalColumn = hasRules && Boolean(leadSection || eligibilitySection);

    return (
        <div className={`faq-block ${hasLegalColumn ? "faq-block--with-legal" : ""} ${sectionOpen ? "faq-block--open" : ""}`}>
            {/*
              * With a legal column the title and intro belong INSIDE it — the
              * anchor's left rail is one run of text (title, lede, rules,
              * eligibility) beside the questions. Without one there is no rail
              * to put them in, so they stay above the block.
              */}
            {!hasLegalColumn && <>
                <h3 className="faq-block__title">{sectionTitle || "Rules & FAQs"}</h3>
                {config.intro && <p className="faq-block__intro">{config.intro}</p>}
            </>}

            <div className="faq-block__layout">
                {hasLegalColumn &&
                    <div className="faq-block__legal">
                        <h3 className="faq-block__title">{sectionTitle || "Rules & FAQs"}</h3>
                        {config.intro && <p className="faq-block__intro">{config.intro}</p>}
                        {leadSection &&
                            <>
                                <p className="faq-block__legal-head faq-block__legal-head--first">
                                    {config.termsHeading || "Official Rules"}
                                </p>
                                {(leadSection.paragraphs ?? []).map((p, i) =>
                                    <p key={`lead-${i}`} className="faq-block__legal-body">{p}</p>)}
                            </>
                        }
                        <button
                            type="button"
                            className="faq-block__rules-link"
                            onClick={() => setRulesOpen(true)}
                        >
                            {config.rulesLabel || "Read Full Official Rules"}
                        </button>
                        {eligibilitySection &&
                            <>
                                <p className="faq-block__legal-head">Eligibility</p>
                                <ul className="faq-block__eligibility">
                                    {(eligibilitySection.paragraphs ?? []).map((p, i) =>
                                        /*
                                         * A leading space or dash marks a sub-point.
                                         * The eligibility list is two levels deep and
                                         * the authored copy carries that nesting in
                                         * its own indentation — four lines do not
                                         * need a tree structure to express.
                                         */
                                        <li
                                            key={`elig-${i}`}
                                            className={(/^[\s-]/).test(p) ? "faq-block__eligibility-sub" : undefined}
                                        >
                                            {p.replace(/^[\s-]+/, "")}
                                        </li>)}
                                </ul>
                            </>
                        }
                    </div>
                }

                <div className="faq-block__questions">
                    {/*
                      * The QUESTIONS fold, not the rules. The rules are the
                      * part a member is entitled to see without asking; the
                      * questions are the long tail, and folded they stop the
                      * section running the length of the page.
                      */}
                    <button
                        type="button"
                        className="faq-block__section-toggle"
                        onClick={() => setSectionOpen(open => !open)}
                        aria-expanded={sectionOpen}
                    >
                        <span className="faq-block__faq-head">FAQ</span>
                        <span className="faq-block__chevron faq-block__chevron--section" aria-hidden="true">
                            <svg viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9" /></svg>
                        </span>
                    </button>

                    {sectionOpen && config.searchable &&
                        <input
                            type="text"
                            className="faq-block__search"
                            placeholder="Search FAQs..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    }

                    {sectionOpen &&
                <div className="faq-block__list">
                    {filteredItems.map(({item, key}) => {
                        const isOpen = openItems.has(key);
                        return (
                            <div key={key} className={`faq-block__item ${isOpen ? "faq-block__item--open" : ""}`}>
                                <button
                                    type="button"
                                    className="faq-block__question"
                                    onClick={() => toggleItem(key)}
                                    aria-expanded={isOpen}
                                >
                                    <span>{item.question}</span>
                                    <span className="faq-block__chevron" aria-hidden="true">
                                        <svg viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9" /></svg>
                                    </span>
                                </button>
                                {isOpen &&
                                    <div
                                        className="faq-block__answer"
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.answer) }}
                                    />
                                }
                            </div>
                        );
                    })}
                </div>
                    }
                </div>
            </div>

            {hasRules &&
                <>
                    {!hasLegalColumn &&
                        <button
                            type="button"
                            className="faq-block__rules-link"
                            onClick={() => setRulesOpen(true)}
                        >
                            {config.rulesLabel || "Read Full Official Rules"}
                        </button>
                    }
                    {rulesOpen &&
                        <SurfaceRulesModal
                            title={config.rulesTitle || "Official Rules"}
                            sections={rulesSections}
                            onClose={() => setRulesOpen(false)}
                        />
                    }
                </>
            }
        </div>
    );
};
